"use client";

import React, { useEffect } from "react";

if (typeof window !== "undefined" && !window.React) {
  window.React = React;
}

class ComponentErrorBoundary extends React.Component<
  { children: React.ReactNode; componentId: string; onRemove?: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: {
    children: React.ReactNode;
    componentId: string;
    onRemove?: () => void;
  }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Remote component error:", error, errorInfo);
    console.error("Component ID:", this.props.componentId);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-red-300 bg-red-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full flex-shrink-0" />
              <h3 className="text-sm font-medium text-red-800">
                Component Error
              </h3>
            </div>
            {this.props.onRemove && (
              <button
                type="button"
                onClick={this.props.onRemove}
                className="text-red-600 hover:text-red-800 text-sm px-2 py-1 border border-red-300 rounded hover:bg-red-100 transition-colors"
              >
                Remove
              </button>
            )}
          </div>
          <p className="text-sm text-red-700 mb-2">
            This component encountered an error and could not be rendered.
          </p>
          <details className="text-xs text-red-600">
            <summary className="cursor-pointer hover:text-red-800">
              Show error details
            </summary>
            <pre className="mt-2 whitespace-pre-wrap bg-red-100 p-2 rounded">
              {this.state.error?.message || "Unknown error"}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

export function RemoteComponent({
  id,
  onRemove,
  ...props
}: {
  id: string;
  onRemove?: () => void;
  [key: string]: unknown;
}) {
  const [Component, setComponent] = React.useState<React.ComponentClass | null>(
    null
  );
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);

    import(/* webpackIgnore: true */ `/api/components/${id}`)
      .then((comp) => {
        const key = Object.keys(comp)[0];
        if (!key) {
          throw new Error("No component found in the remote module");
        }
        setComponent(() => comp[key]);
      })
      .catch((error) => {
        console.error("Error loading remote component:", error);
        setLoadError(error.message || "Failed to load component");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-600">Loading component...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="border border-yellow-300 bg-yellow-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded-full flex-shrink-0" />
            <h3 className="text-sm font-medium text-yellow-800">
              Loading Error
            </h3>
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-yellow-600 hover:text-yellow-800 text-sm px-2 py-1 border border-yellow-300 rounded hover:bg-yellow-100 transition-colors"
            >
              Remove
            </button>
          )}
        </div>
        <p className="text-sm text-yellow-700 mb-2">
          Failed to load the component.
        </p>
        <details className="text-xs text-yellow-600">
          <summary className="cursor-pointer hover:text-yellow-800">
            Show error details
          </summary>
          <pre className="mt-2 whitespace-pre-wrap bg-yellow-100 p-2 rounded">
            {loadError}
          </pre>
        </details>
      </div>
    );
  }

  if (!Component) {
    return (
      <div className="border border-gray-300 bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-500 rounded-full flex-shrink-0" />
            <h3 className="text-sm font-medium text-gray-800">
              Component Not Found
            </h3>
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-gray-600 hover:text-gray-800 text-sm px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
            >
              Remove
            </button>
          )}
        </div>
        <p className="text-sm text-gray-700">
          The requested component could not be found.
        </p>
      </div>
    );
  }

  return (
    <ComponentErrorBoundary componentId={id} onRemove={onRemove}>
      <Component {...props} />
    </ComponentErrorBoundary>
  );
}
